import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Star, Calendar, MessageSquare, ArrowLeft, Check, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom images for specific coaches
const COACH_IMAGES = {
  'founder-crystal-nicole': 'https://customer-assets.emergentagent.com/job_1d2f3656-52d0-4fe2-8a37-9f88482f6558/artifacts/jnhn37h5_crystal%20and%20Dr.%20Nicole%20image.png'
};

const CoachProfilePage = () => {
  const { coachId } = useParams();
  const { user, hasTier } = useAuth();
  const navigate = useNavigate();
  const [coach, setCoach] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [matchedCoachId, setMatchedCoachId] = useState(null);
  const [canBookWithCoach, setCanBookWithCoach] = useState(true);

  // Check if user is Inner Edge only (must book with assigned coach)
  const isInnerEdgeOnly = user && hasTier('inner_edge') && !hasTier('sdc') && !hasTier('coaching_activation');

  useEffect(() => {
    fetchCoach();
    fetchReviews();
    if (isInnerEdgeOnly) {
      checkMatchedCoach();
    }
  }, [coachId, isInnerEdgeOnly]);

  const fetchCoach = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/coaches/${coachId}`);
      setCoach(response.data);
    } catch (error) {
      console.error('Failed to fetch coach:', error);
      toast.error('Coach not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/coaches/${coachId}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  const checkMatchedCoach = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/diagnostic/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // The API returns matched coach at coach_match.coach
      const matchedId = response.data?.coach_match?.coach?.id;
      setMatchedCoachId(matchedId);
      // Can only book if this coach is the matched coach
      setCanBookWithCoach(matchedId === coachId);
    } catch (error) {
      console.error('Failed to check matched coach:', error);
      setCanBookWithCoach(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please log in to leave a review');
      navigate('/login');
      return;
    }

    setSubmittingReview(true);
    try {
      await axios.post(`${API_URL}/api/coaches/${coachId}/reviews`, {
        rating: newReview.rating,
        comment: newReview.comment
      });
      toast.success('Review submitted successfully!');
      setNewReview({ rating: 5, comment: '' });
      fetchReviews();
      fetchCoach();
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleMessageCoach = () => {
    if (!user) {
      toast.error('Please log in to message this coach');
      navigate('/login');
      return;
    }
    navigate(`/messages/${coachId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 py-32">
          <div className="animate-pulse">
            <div className="h-64 bg-sand rounded-xl mb-8" />
            <div className="h-8 bg-sand rounded w-1/3 mb-4" />
            <div className="h-4 bg-sand rounded w-1/4 mb-8" />
            <div className="h-32 bg-sand rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!coach) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 py-32 text-center">
          <h1 className="font-heading text-3xl text-forest mb-4">Coach Not Found</h1>
          <Link to="/coaches">
            <Button className="btn-primary">Back to Coaches</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      
      <main className="pt-20 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/coaches" className="inline-flex items-center gap-2 text-forest hover:text-terracotta mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to Coaches
          </Link>

          {/* Profile Header */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8" data-testid="coach-profile">
            <div className="hero-gradient h-32 md:h-48" />
            <div className="px-6 pb-6 -mt-16 md:-mt-20">
              <div className="flex flex-col md:flex-row md:items-end gap-6">
                <Avatar className="h-32 w-32 md:h-40 md:w-40 border-4 border-white shadow-lg">
                  <AvatarImage src={COACH_IMAGES[coachId] || coach.image_url} alt={coach.name} className="object-cover" />
                  <AvatarFallback className="bg-forest text-white text-4xl">
                    {coach.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h1 className="font-heading text-3xl md:text-4xl font-bold text-forest">{coach.name}</h1>
                      <p className="text-terracotta font-medium text-lg">{coach.title}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-5 w-5 ${i < Math.round(coach.average_rating) ? 'text-terracotta fill-terracotta' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                        <span className="font-semibold text-forest">{coach.average_rating}</span>
                        <span className="text-gray-500">({coach.total_reviews} reviews)</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      {canBookWithCoach && (
                        <Button onClick={handleMessageCoach} variant="outline" className="btn-outline" data-testid="message-coach-btn">
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Message
                        </Button>
                      )}
                      {isInnerEdgeOnly && !canBookWithCoach ? (
                        <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg">
                          <Lock className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-500">Book with your assigned coach only</span>
                        </div>
                      ) : (
                        <Link to={coach.calendly_link || "/packages"} target={coach.calendly_link ? "_blank" : "_self"}>
                          <Button className="btn-primary" data-testid="book-session-btn">
                            <Calendar className="h-4 w-4 mr-2" />
                            Book Session
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* About */}
              <Card className="card-base">
                <CardContent className="p-6">
                  <h2 className="font-heading text-2xl font-semibold text-forest mb-4">About</h2>
                  <p className="text-gray-600 leading-relaxed whitespace-pre-line">{coach.bio}</p>
                </CardContent>
              </Card>

              {/* Reviews */}
              <Card className="card-base">
                <CardContent className="p-6">
                  <h2 className="font-heading text-2xl font-semibold text-forest mb-6">Reviews</h2>
                  
                  {/* Review Form */}
                  {user && (
                    <form onSubmit={handleSubmitReview} className="mb-8 p-4 bg-sand/50 rounded-xl">
                      <h3 className="font-medium text-forest mb-4">Leave a Review</h3>
                      <div className="mb-4">
                        <label className="block text-sm text-gray-600 mb-2">Rating</label>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setNewReview(prev => ({ ...prev, rating: star }))}
                              className="focus:outline-none"
                            >
                              <Star
                                className={`h-8 w-8 transition-colors ${
                                  star <= newReview.rating
                                    ? 'text-terracotta fill-terracotta'
                                    : 'text-gray-300 hover:text-terracotta/50'
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="mb-4">
                        <Textarea
                          placeholder="Share your experience..."
                          value={newReview.comment}
                          onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                          className="input-base min-h-24"
                          required
                          data-testid="review-comment-input"
                        />
                      </div>
                      <Button type="submit" className="btn-primary" disabled={submittingReview} data-testid="submit-review-btn">
                        {submittingReview ? 'Submitting...' : 'Submit Review'}
                      </Button>
                    </form>
                  )}

                  {/* Reviews List */}
                  {reviews.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No reviews yet. Be the first to review!</p>
                  ) : (
                    <div className="space-y-6">
                      {reviews.map((review) => (
                        <div key={review.id} className="border-b border-sand pb-6 last:border-0" data-testid={`review-${review.id}`}>
                          <div className="flex items-start gap-4">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-forest text-white">
                                {review.user_name?.charAt(0)?.toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-forest">{review.user_name}</h4>
                                <span className="text-sm text-gray-500">
                                  {new Date(review.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 my-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`h-4 w-4 ${i < review.rating ? 'text-terracotta fill-terracotta' : 'text-gray-300'}`}
                                  />
                                ))}
                              </div>
                              <p className="text-gray-600 mt-2">{review.comment}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Credentials */}
              {coach.credentials?.length > 0 && (
                <Card className="card-base">
                  <CardContent className="p-6">
                    <h3 className="font-heading text-lg font-semibold text-forest mb-4">Credentials</h3>
                    <div className="space-y-2">
                      {coach.credentials.map((cred, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-terracotta" />
                          <span className="text-gray-600">{cred}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Specialties */}
              <Card className="card-base">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-forest mb-4">Specialties</h3>
                  <div className="flex flex-wrap gap-2">
                    {coach.specialties?.map((specialty, i) => (
                      <span key={i} className="px-3 py-1.5 bg-sand text-forest text-sm rounded-full">
                        {specialty}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* CTA */}
              <Card className="card-base bg-forest text-white">
                <CardContent className="p-6 text-center">
                  <h3 className="font-heading text-xl font-semibold mb-2">Ready to Start?</h3>
                  <p className="text-white/80 mb-4">Book a session with {coach.name.split(' ')[0]} today.</p>
                  <Link to="/packages">
                    <Button className="w-full bg-white text-forest hover:bg-white/90">
                      View Packages
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CoachProfilePage;
